import {
  deriveBrandPalette,
  resolveAccentColor,
  resolveTypeface,
  type TypefaceWhitelistEntry,
} from "@lifter/hub-ui/browser-shared";
import { useEffect } from "react";

/** The token cascade: everything that resolves to a CSS custom property. */
interface BrandCascade {
  accentColor?: string | null;
  typefaceId?: string | null;
}

interface BrandStyleInjectorProps {
  branding: BrandCascade & { logoUrl?: string | null };
}

// Loaded Google Font hrefs cached across mounts so a tenant switch never
// re-injects the same <link>. Module-scoped because there is one Hub document.
const loadedFontHrefs = new Set<string>();

function ensureFontLoaded(entry: TypefaceWhitelistEntry): void {
  if (typeof document === "undefined") return;
  const familyParam = entry.googleFontsName.replace(/ /g, "+");
  const axis = entry.googleFontsAxis ? `:${entry.googleFontsAxis}` : "";
  const href = `https://fonts.googleapis.com/css2?family=${familyParam}${axis}&display=swap`;
  if (loadedFontHrefs.has(href)) return;
  loadedFontHrefs.add(href);
  ensurePreconnect("https://fonts.googleapis.com");
  ensurePreconnect("https://fonts.gstatic.com", true);
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.setAttribute("data-lifter-font", entry.id);
  document.head.appendChild(link);
}

function ensurePreconnect(href: string, crossOrigin = false): void {
  if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preconnect";
  link.href = href;
  if (crossOrigin) link.crossOrigin = "anonymous";
  document.head.appendChild(link);
}

// Mobile browser chrome (address bar / task switcher) and the PWA splash read
// the <meta name="theme-color"> content, not our CSS vars — so the tenant accent
// only reaches them if we mirror it onto the tag here. The static tag in
// index.html seeds the brand-less default; this keeps it in step on tenant
// switch (and creates it if a white-label index dropped it). Exported so the
// DOM-mutation contract can be unit-tested without standing up a React renderer.
export function ensureThemeColor(color: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  if (meta.content !== color) meta.content = color;
}

const BRAND_ICON_ATTR = "data-lifter-brand-icon";
const DEFAULT_ICON_STASH_ATTR = "data-lifter-default-icons";

/**
 * Tab icon. index.html ships several default `rel="icon"` links (.ico/.svg/.png)
 * and a browser may prefer any of them, so a tenant logo only wins once the
 * defaults leave the document. They are parked in an inert <template> — its
 * content is a detached fragment the browser never applies — rather than
 * dropped, so removing the tenant logo restores exactly the set index.html
 * shipped instead of a list this module would have to keep in step with it.
 *
 * `apple-touch-icon` and the PWA manifest stay Lifter's: per-tenant manifest
 * icons are their own piece of work.
 */
function ensureBrandFavicon(logoUrl: string | null | undefined): void {
  if (typeof document === "undefined") return;
  const head = document.head;
  const stash = head.querySelector<HTMLTemplateElement>(`template[${DEFAULT_ICON_STASH_ATTR}]`);
  const branded = head.querySelector<HTMLLinkElement>(`link[${BRAND_ICON_ATTR}]`);

  if (!logoUrl) {
    branded?.remove();
    if (stash) {
      head.append(...stash.content.childNodes);
      stash.remove();
    }
    return;
  }

  const defaults = [...head.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')].filter(
    (link) => link !== branded,
  );
  if (defaults.length > 0) {
    const target = stash ?? document.createElement("template");
    target.setAttribute(DEFAULT_ICON_STASH_ATTR, "");
    target.content.append(...defaults);
    if (!stash) head.append(target);
  }

  if (branded) {
    if (branded.getAttribute("href") !== logoUrl) branded.setAttribute("href", logoUrl);
    return;
  }
  const link = document.createElement("link");
  link.setAttribute("rel", "icon");
  link.setAttribute("href", logoUrl);
  link.setAttribute(BRAND_ICON_ATTR, "");
  head.append(link);
}

export function applyBrandingToDocument(branding: BrandCascade): void {
  if (typeof document === "undefined") return;
  const accent = resolveAccentColor({ accentColor: branding.accentColor });
  const palette = deriveBrandPalette(accent);
  const typeface = resolveTypeface(branding.typefaceId);
  const root = document.documentElement;
  const setVar = (key: string, value: string) => {
    if (root.style.getPropertyValue(key) !== value) root.style.setProperty(key, value);
  };
  setVar("--brand", palette.brand);
  setVar("--brand-dark", palette.brandDark);
  setVar("--brand-soft", palette.brandSoft);
  setVar("--brand-soft-2", palette.brandSoft2);
  setVar("--brand-ring", palette.brandRing);
  setVar("--brand-contrast", palette.brandContrast);
  setVar("--brand-accent", palette.brand);
  setVar("--brand-accent-contrast", palette.brandContrast);
  setVar("--font-body", typeface.bodyFamily);
  setVar("--font-display", typeface.displayFamily);
  document.body.setAttribute("data-tenant-display", typeface.pair);
  ensureThemeColor(palette.brand);
  ensureFontLoaded(typeface);
}

export function BrandStyleInjector({ branding }: BrandStyleInjectorProps): null {
  const { accentColor, logoUrl, typefaceId } = branding;
  useEffect(() => {
    applyBrandingToDocument({ accentColor, typefaceId });
  }, [accentColor, typefaceId]);
  // Separate from the cascade: the branding form previews an accent and a
  // typeface against the live document, and it has no logo to preview there.
  useEffect(() => {
    ensureBrandFavicon(logoUrl);
  }, [logoUrl]);

  return null;
}
